/**
 * Color de los avatares de iniciales.
 *
 * El tono sale del nombre, para que cada persona tenga el suyo y siempre el
 * mismo. Lo que NO puede ser fijo es la claridad del texto: a igual claridad,
 * el contraste depende del tono — un amarillo al 38 % se ve mucho más claro
 * que un azul al 38 % — y con un 38 % para todos las iniciales de buena parte
 * de la gente quedaban por debajo de 3:1 sobre el fondo pastel.
 *
 * Acá la claridad se baja hasta que el contraste llega al mínimo legible.
 *
 * @module utils/avatarColor
 */

/** Contraste mínimo (WCAG AA para texto normal). */
const MINIMO = 4.5

/** Claridad de partida del texto, en porcentaje. */
const CLARIDAD_INICIAL = 38

/** Saturaciones y claridad del fondo, que no cambian. */
const SAT_TEXTO = 55
const SAT_FONDO = 70
const CLARIDAD_FONDO = 92

/**
 * Convierte HSL a RGB.
 *
 * @param {number} h - Tono, 0-360.
 * @param {number} s - Saturación, 0-100.
 * @param {number} l - Claridad, 0-100.
 * @returns {number[]} [r, g, b] de 0 a 255.
 */
export const hslARgb = (h, s, l) => {
  const sn = s / 100
  const ln = l / 100
  const c = (1 - Math.abs(2 * ln - 1)) * sn
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = ln - c / 2
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x]
  return [r, g, b].map((v) => Math.round((v + m) * 255))
}

/**
 * Luminancia relativa (WCAG) de un color RGB.
 *
 * @param {number[]} rgb - [r, g, b] de 0 a 255.
 * @returns {number}
 */
export const luminancia = ([r, g, b]) => {
  const canal = (x) => {
    const v = x / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b)
}

/**
 * Relación de contraste entre dos colores RGB.
 *
 * @param {number[]} a - [r, g, b].
 * @param {number[]} b - [r, g, b].
 * @returns {number} De 1 (iguales) a 21 (negro sobre blanco).
 */
export const contraste = (a, b) => {
  const la = luminancia(a)
  const lb = luminancia(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Tono estable derivado del nombre.
 *
 * @param {string} nombre
 * @returns {number} 0-359.
 */
export const tonoDeNombre = (nombre) =>
  [...(nombre || '')].reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % 360

/**
 * Colores del avatar de iniciales de una persona.
 *
 * @param {string} nombre - Nombre completo.
 * @returns {{color: string, background: string, contraste: number}}
 */
export const coloresAvatar = (nombre) => {
  const tono = tonoDeNombre(nombre)
  const fondo = hslARgb(tono, SAT_FONDO, CLARIDAD_FONDO)

  let claridad = CLARIDAD_INICIAL
  while (
    claridad > 8 &&
    contraste(hslARgb(tono, SAT_TEXTO, claridad), fondo) < MINIMO
  ) {
    claridad -= 1
  }

  return {
    color: `hsl(${tono} ${SAT_TEXTO}% ${claridad}%)`,
    background: `hsl(${tono} ${SAT_FONDO}% ${CLARIDAD_FONDO}%)`,
    contraste: contraste(hslARgb(tono, SAT_TEXTO, claridad), fondo),
  }
}
